import { useCallback } from 'react'
import styled from 'styled-components'

import { useDialog } from '@loadsmart/loadsmart-ui'
import { Layout, Text, Card, Tag, Button } from '@loadsmart/miranda-react'

import { type StopAppointment } from 'core/appointments/domain/Appointment'
import {
  formatAppointmentStatus,
  formatAppointmentDescription,
  formatAppointmentStatusColor,
} from 'core/appointments/presenters/appointmentPresenter'
import { Icon } from 'core/ui/components/icon/Icon'

import openDockLogo from '../../assets/opendock-logo.png'
import { getAppointmentSlots } from '../../domain/Appointment'
import { type Facility } from '../../domain/Facility'
import {
  formatMisschedulingReason,
  formatScheduleDate,
  formatStatusTagColor,
  formatActionButtonVariant,
  formatActionLabel,
} from '../../presenters/appointmentPresenter'
import { AppointmentDialog } from '../AppointmentDialog/AppointmentDialog'
import { OpenDockAppointmentDialog } from '../OpenDockAppointmentDialog/OpenDockAppointmentDialog'
import { AppointmentCardEmptyState } from './AppointmentCardEmptyState'
import { MissingDate } from './MissingDate'
import { SchedulingStatusTag } from './SchedulingStatusTag'

export type AppointmentCardProps = {
  shipmentUUID: string
  stopIndex: number
  stopDate: string
  appointment?: StopAppointment
  facilityName?: string
  warehouseUUID?: string | null
  onConfirm: (uuid: string) => Promise<void>
  facility?: Partial<Facility>
}

const CapitalizedText = styled(Text)`
  text-transform: capitalize;
`

const UppercasedText = styled(Text)`
  text-transform: uppercase;
`

export function AppointmentCard({
  shipmentUUID,
  stopIndex,
  stopDate,
  appointment,
  facility,
  onConfirm,
}: AppointmentCardProps) {
  const { open, show, hide } = useDialog({ open: false })
  const showOpendockLogo = Boolean(facility?.warehouseUuid)

  const appointmentDialogIsOpen = Boolean(open && !facility?.warehouseUuid)
  const openDockAppointmentIsOpen = Boolean(open && facility?.warehouseUuid)

  const handleConfirm = useCallback(() => {
    onConfirm(shipmentUUID)
    hide()
  }, [hide, onConfirm, shipmentUUID])

  if (!appointment) {
    return (
      <>
        <AppointmentCardEmptyState
          onButtonClick={show}
          showOpendockLogo={showOpendockLogo}
          disabled={!facility}
        />
        <AppointmentDialog
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          stopDate={stopDate}
          isOpen={appointmentDialogIsOpen}
          onClose={hide}
          onConfirm={handleConfirm}
        />
        <OpenDockAppointmentDialog
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          stopDate={stopDate}
          facilityName={facility?.companyName}
          warehouseUUID={facility?.warehouseUuid}
          isOpen={openDockAppointmentIsOpen}
          onClose={hide}
          onConfirm={handleConfirm}
        />
      </>
    )
  }

  const { date: appointmentDate, time: appointmentTime } = getAppointmentSlots(appointment)

  const displayFooter = appointment.scheduledBy || appointment.confirmationNumber

  const cardStatusColor = formatAppointmentStatusColor(appointment.status)
  const tagStatusVariant = formatStatusTagColor(appointment.status)

  function handleOpenAppointment() {
    show()
  }

  return (
    <>
      <Layout.Stack>
        <Text variant="heading-sm-bold">Schedule appointment</Text>
        <Card flagged flag-color={cardStatusColor}>
          <Card.Body>
            <Layout.Group justify="space-between" align="center">
              <Layout.Group align="center">
                <Icon name="clock" width={22} height={22} />
                <Layout.Stack gap="spacing-1">
                  <Layout.Group gap="spacing-1">
                    <Tag variant={tagStatusVariant} size="small" style={{ display: 'block' }}>
                      {formatAppointmentStatus(appointment.status)}
                    </Tag>
                    <SchedulingStatusTag appointment={appointment} />
                  </Layout.Group>
                  {appointmentDate && appointmentTime ? (
                    <>
                      <Text variant="body-md-bold" color="color-text-tertiary">
                        {formatScheduleDate(appointmentDate)}
                      </Text>
                      <div>
                        <Text variant="heading-md" color="color-text-primary">
                          {formatAppointmentDescription(appointment)}{' '}
                        </Text>
                        <UppercasedText variant="heading-md" color="color-text-primary">
                          {`(${appointment.mode} -`}
                        </UppercasedText>
                        <CapitalizedText variant="heading-md" color="color-text-primary">
                          {`${appointment.loadingType})`}
                        </CapitalizedText>
                      </div>
                    </>
                  ) : (
                    <MissingDate />
                  )}
                  <Layout.Stack gap="spacing-0-5">
                    {appointment.misschedulingReason && (
                      <Text variant="body-sm" color="color-warning-100">
                        <CapitalizedText color="color-warning-100" variant="body-sm">
                          Reason: {formatMisschedulingReason(appointment.misschedulingReason)}
                        </CapitalizedText>{' '}
                        {appointment.misschedulingNote && `- ${appointment.misschedulingNote}`}
                      </Text>
                    )}
                    {displayFooter && (
                      <Text variant="body-sm" color="color-text-tertiary">
                        {appointment.scheduledBy && `Scheduled by ${appointment.scheduledBy} `}
                        {appointment.confirmationNumber &&
                          `- Confirmation number ${appointment.confirmationNumber}`}
                      </Text>
                    )}
                  </Layout.Stack>
                </Layout.Stack>
              </Layout.Group>
              <Layout.Stack align="flex-end">
                {showOpendockLogo && <img src={openDockLogo} alt="OpenDock" width={105} />}
                <Button
                  type="button"
                  variant={formatActionButtonVariant(appointment.status)}
                  size="small"
                  onClick={handleOpenAppointment}
                >
                  {formatActionLabel(appointment.status)}
                </Button>
              </Layout.Stack>
            </Layout.Group>
          </Card.Body>
        </Card>
      </Layout.Stack>
      <AppointmentDialog
        shipmentUUID={shipmentUUID}
        stopIndex={stopIndex}
        stopDate={stopDate}
        appointment={appointment}
        isOpen={appointmentDialogIsOpen}
        onClose={hide}
        onConfirm={handleConfirm}
      />
      <OpenDockAppointmentDialog
        shipmentUUID={shipmentUUID}
        stopIndex={stopIndex}
        stopDate={stopDate}
        appointment={appointment}
        facilityName={facility?.companyName}
        warehouseUUID={facility?.warehouseUuid}
        isOpen={openDockAppointmentIsOpen}
        onClose={hide}
        onConfirm={handleConfirm}
      />
    </>
  )
}
